import React, { useEffect, useRef, useState } from 'react';

const BASE = '/flow-assets/content/farm';

function Caption({ step, title, children }) {
  return (
    <figcaption className="mt-4 flex gap-4">
      <span aria-hidden="true" className="font-heading text-2xl leading-none text-clay lining-nums tabular-nums">{step}</span>
      <span>
        <span className="block font-heading text-2xl leading-none text-gray-900">{title}</span>
        <span className="block mt-2 text-gray-600 leading-relaxed max-w-md">{children}</span>
      </span>
    </figcaption>
  );
}

// Muted loop that only plays while on screen. With reduced motion it stays on the poster
// until someone presses play; the button covers WCAG 2.2.2 (pause moving content).
function LoopVideo({ src, poster, label, className }) {
  const videoRef = useRef(null);
  const userPaused = useRef(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !userPaused.current) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const toggle = () => {
    const video = videoRef.current;
    if (video.paused) {
      userPaused.current = false;
      video.play().catch(() => {});
    } else {
      userPaused.current = true;
      video.pause();
    }
  };

  return (
    <div className="relative">
      <video
        ref={videoRef}
        className={className}
        src={src}
        poster={poster}
        aria-label={label}
        muted
        loop
        playsInline
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <button
        type="button"
        onClick={toggle}
        className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-gray-900/75 px-4 py-2 text-sm font-semibold text-gray-50 transition-transform active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2"
      >
        <svg aria-hidden="true" viewBox="0 0 16 16" className="w-3.5 h-3.5 fill-current">
          {playing ? <path d="M4 2h3v12H4zM9 2h3v12H9z" /> : <path d="M4 2l10 6-10 6z" />}
        </svg>
        {playing ? 'Pause' : 'Play'}
      </button>
    </div>
  );
}

// The crop in the order it happens, all shot at Mubarak Farm. Two staggered rows
// around one full-bleed drying-bed photo.
// Size follows the level so the heading matches its neighbours (h3 inside OurStory, h2 on /about)
const HEADING_SIZE = {
  h2: 'text-4xl sm:text-5xl',
  h3: 'text-3xl sm:text-4xl lg:text-5xl',
};

export default function FarmGallery({ headingAs: Heading = 'h3' }) {
  return (
    <div className="overflow-x-clip">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto mb-12 lg:mb-16">
          <Heading className={`font-heading ${HEADING_SIZE[Heading]} leading-[1.05] tracking-tight text-gray-900`}>
            From branch to sack
          </Heading>
          <p className="mt-4 text-lg text-gray-700 max-w-xl leading-relaxed">
            Every photo and clip here was taken at Mubarak Farm in Nabutaka.
          </p>
        </div>

        <ol className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-x-6 lg:gap-x-8 gap-y-14 lg:gap-y-20">
          <li className="lg:col-span-7">
            <figure>
              <img
                className="w-full aspect-[4/3] sm:aspect-[4/5] lg:aspect-[4/3] object-cover rounded-2xl"
                src={`${BASE}/robusta-cherry-clusters-800.webp`}
                srcSet={`${BASE}/robusta-cherry-clusters-800.webp 800w, ${BASE}/robusta-cherry-clusters-1500.webp 1500w`}
                sizes="(min-width: 1024px) 58vw, (min-width: 640px) 50vw, 100vw"
                width="1500"
                height="1125"
                alt="Dense clusters of ripe red and orange Robusta cherries along the branches"
                loading="lazy"
              />
              <Caption step="1" title="On the branch">Robusta ripens in tight clusters along each branch.</Caption>
            </figure>
          </li>

          <li className="lg:col-start-9 lg:col-span-4 lg:mt-32">
            <figure>
              <img
                className="w-full aspect-[3/4] sm:aspect-[4/5] lg:aspect-[3/4] object-cover rounded-2xl"
                src={`${BASE}/ripe-cherries-in-hand-600.webp`}
                srcSet={`${BASE}/ripe-cherries-in-hand-600.webp 600w, ${BASE}/ripe-cherries-in-hand-1200.webp 1200w`}
                sizes="(min-width: 640px) 50vw, 100vw"
                width="1200"
                height="1600"
                alt="An open hand holding a few ripe red coffee cherries over red soil"
                loading="lazy"
              />
              <Caption step="2" title="Picked by hand">Ripe cherries, just off the tree.</Caption>
            </figure>
          </li>

          {/* Full bleed: breaks out of the container to the viewport edges */}
          <li className="sm:col-span-2 lg:col-span-12 ml-[calc(50%-50vw)] w-screen">
            <figure>
              <img
                className="w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-[2/1] object-cover"
                src={`${BASE}/drying-yard-1200.webp`}
                srcSet={`${BASE}/drying-yard-1200.webp 1200w, ${BASE}/drying-yard-1600.webp 1600w, ${BASE}/drying-yard-2400.webp 2400w`}
                sizes="100vw"
                width="2400"
                height="1350"
                alt="Coffee cherries spread out to dry on a raised bed in the sun, with a fence and trees behind"
                loading="lazy"
              />
              <div className="container mx-auto px-4 lg:px-8">
                <div className="max-w-6xl mx-auto">
                  <Caption step="3" title="Dried in the sun">Whole cherries dry on raised beds until the skins turn dark.</Caption>
                </div>
              </div>
            </figure>
          </li>

          <li className="lg:col-start-2 lg:col-span-4 lg:mt-32">
            <figure>
              <img
                className="w-full aspect-[3/4] sm:aspect-[4/5] lg:aspect-[3/4] object-cover rounded-2xl"
                src={`${BASE}/green-beans-in-hand-600.webp`}
                srcSet={`${BASE}/green-beans-in-hand-600.webp 600w, ${BASE}/green-beans-in-hand-1200.webp 1200w`}
                sizes="(min-width: 640px) 50vw, 100vw"
                width="1200"
                height="1600"
                alt="A handful of hulled green Robusta beans"
                loading="lazy"
              />
              <Caption step="4" title="Hulled">Once dry, the husk comes off and leaves the green bean.</Caption>
            </figure>
          </li>

          <li className="lg:col-start-7 lg:col-span-6">
            <figure>
              <LoopVideo
                className="w-full aspect-[4/5] object-cover rounded-2xl bg-gray-200"
                src={`${BASE}/green-beans-in-sack.mp4`}
                poster={`${BASE}/green-beans-in-sack-poster.webp`}
                label="Hands running through green Robusta beans in a woven sack"
              />
              <Caption step="5" title="In the sack">Green Robusta in a woven sack.</Caption>
            </figure>
          </li>
        </ol>
      </div>
    </div>
  );
}
